import { useCallback, useState } from 'react';
import { ERROR_MESSAGES } from '../constants';

interface ErrorState {
  error: string | null;
  hasError: boolean;
}

export const useErrorHandler = () => {
  const [errorState, setErrorState] = useState<ErrorState>({
    error: null,
    hasError: false,
  });

  const setError = useCallback((error: string | Error | null) => {
    const errorMessage = error instanceof Error ? error.message : error;
    setErrorState({
      error: errorMessage,
      hasError: !!errorMessage,
    });

    if (errorMessage) {
      console.error('Error occurred:', errorMessage);
    }
  }, []);

  const clearError = useCallback(() => {
    setErrorState({
      error: null,
      hasError: false,
    });
  }, []);

  const handleAsyncError = useCallback(async <T>(
    asyncFn: () => Promise<T>,
    fallbackMessage?: string
  ): Promise<T | null> => {
    try {
      return await asyncFn();
    } catch (error) {
      const errorMessage = error instanceof Error
        ? error.message
        : fallbackMessage || ERROR_MESSAGES.FAILED_TO_CONNECT;
      setError(errorMessage);
      return null;
    }
  }, [setError]);

  const withErrorHandling = useCallback(<T extends any[], R>(
    fn: (...args: T) => R | Promise<R>,
    fallbackMessage?: string
  ) => {
    return async (...args: T): Promise<R | null> => {
      try {
        const result = await fn(...args);
        clearError();
        return result;
      } catch (error) {
        const errorMessage = error instanceof Error
          ? error.message
          : fallbackMessage || ERROR_MESSAGES.FAILED_TO_CONNECT;
        setError(errorMessage);
        return null;
      }
    };
  }, [setError, clearError]);

  return {
    error: errorState.error,
    hasError: errorState.hasError,
    setError,
    clearError,
    handleAsyncError,
    withErrorHandling,
  };
};
